import { Injectable } from '@angular/core'
import { filter, map, tap } from 'rxjs/operators'
import { TextSearchDestination, TextSearchIndexGQL, TextSearchListing } from '../../graphql'
import { textSearchStorageKeys } from '../constant'
import { UnsubscribeOnDestroy } from '../utility'

export interface TextSearchResults {
    properties: TextSearchListing[],
    destinations: TextSearchDestination[],
}

@Injectable({
    providedIn: 'root'
})
export class ListingSearchService extends UnsubscribeOnDestroy {
    constructor(
        private _textSearchIndexGQL: TextSearchIndexGQL,
    ) {
        super()
        this.getSearchTextListings()
    }

    listings: TextSearchListing[] = []
    destinations: TextSearchDestination[] = []

    private getSearchTextListings() {
        const properties = sessionStorage.getItem(textSearchStorageKeys.textSearchProperties)
        const destinations = sessionStorage.getItem(textSearchStorageKeys.textSearchDestinations)

        if (properties && destinations) {
            this.listings = JSON.parse(properties)
            this.destinations = JSON.parse(destinations)
            return
        }

        this._textSearchIndexGQL
            .fetch()
            .pipe(
                filter(result => !result.loading),
                tap(result => {
                    if (result.errors?.length) {
                        throw new Error(`Failed to fetch search text results: ${result.errors[0].message}`)
                    }
                }),
                map(result => result.data.textSearchIndex),
            ).subscribe(result => {

            this.listings = result.listings // sort alphabetically
                .filter(listing => listing.name)
                .sort((a, b) => {
                    if (a.name < b.name) { return -1 }
                    if (a.name > b.name) { return 1 }
                    return 0
                })

            this.destinations = result.destinations // remove duplicates and sort alphabetically
                .filter(d => d.name)
                .sort((a: any, b: any) => {
                    if (a.name < b.name) { return -1 }
                    if (a.name > b.name) { return 1 }
                    return 0
                })

            sessionStorage.setItem(textSearchStorageKeys.textSearchProperties, JSON.stringify(this.listings))
            sessionStorage.setItem(textSearchStorageKeys.textSearchDestinations, JSON.stringify(this.destinations))
        })
    }

    filterListings(searchTerm: string): TextSearchResults {
        const filteredProperties = this.listings.filter((property) => {
            const propertyName = property.name.toLowerCase()
            const locationName = property.locationName.toLowerCase()
            const operatornName = property?.operatorName?.toLowerCase() ?? ''
            const nonDisplayFields = property?.nonDisplayFields?.toLowerCase() ?? ''
            return this.starstWithOrIncludes(propertyName, searchTerm) ||
            this.starstWithOrIncludes(locationName, searchTerm) ||
            this.starstWithOrIncludes(operatornName, searchTerm) ||
            this.starstWithOrIncludes(nonDisplayFields, searchTerm)
        })
        const destinationsFiltered = this.destinations.filter((destination: any) => {
            const destinationName = destination.name.toLowerCase()
            return destinationName.startsWith(searchTerm) || (destinationName.includes(searchTerm))
        })

        return {
            properties: filteredProperties,
            destinations: destinationsFiltered,
        }
    }

    private starstWithOrIncludes(propertyName: string, searchTerm: string) {
        return propertyName.startsWith(searchTerm) || (propertyName.includes(searchTerm))
    }
}
